import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, ExternalLink, ChevronLeft, ChevronRight, Filter, Check } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import LeadModal from './leads/LeadModal';
import { StatusBadge } from './StatusBadge';

const API_URL = getApiUrl();

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// V36 Phase 2 — Backend-side column filters. Values are sent to the
// /api/marketing/dashboard/leads endpoint and applied BEFORE pagination so
// filters span ALL pages, not just the loaded page.
const TRIAL_STATUS_OPTIONS = [
  { value: 'pending',   label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show',   label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
];
const PAYMENT_OPTIONS = [
  { value: 'paid',   label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];
const SOURCE_OPTIONS = [
  { value: 'Performance Marketing', label: 'Performance Marketing' },
  { value: 'Social Media',          label: 'Social Media' },
  { value: 'Website',               label: 'Website' },
  { value: 'Quick Payment Link',    label: 'Direct Payment Link' },
  { value: 'Referral',              label: 'Referral' },
  { value: 'Doctor Referral',       label: 'Doctor Referral' },
  { value: 'Direct',                label: 'Direct' },
];

// V36 — Column filter dropdown component. Closes on outside click.
const ColumnFilter = ({ label, options, selected, onChange, testId, mode, onModeChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v) => {
    const next = selected.includes(v)
      ? selected.filter(x => x !== v)
      : [...selected, v];
    onChange(next);
  };
  const clear = () => onChange([]);
  const hasMode = typeof onModeChange === 'function';

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`p-0.5 rounded hover:bg-slate-200 transition-colors ${selected.length > 0 ? 'text-indigo-600' : 'text-slate-400'}`}
        title={selected.length > 0 ? `${selected.length} filter${selected.length !== 1 ? 's' : ''} active` : 'Filter'}
        data-testid={`${testId}-toggle`}
      >
        <Filter className="w-3.5 h-3.5" fill={selected.length > 0 ? 'currentColor' : 'none'} />
      </button>
      {open && (
        <div className="absolute left-0 mt-1 w-52 bg-white rounded-md shadow-lg border border-slate-200 z-50 py-1"
             data-testid={`${testId}-dropdown`}>
          <div className="px-3 py-1.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-700 uppercase">{label}</span>
            {selected.length > 0 && (
              <button onClick={clear} className="text-[10px] text-indigo-600 hover:text-indigo-800" data-testid={`${testId}-clear`}>
                Clear
              </button>
            )}
          </div>
          {hasMode && (
            <div className="px-2 py-2 border-b border-slate-100">
              <div className="flex rounded-md border border-slate-200 overflow-hidden text-[11px] font-medium">
                <button
                  onClick={() => onModeChange('in')}
                  className={`flex-1 px-2 py-1 transition-colors ${mode !== 'not_in' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`${testId}-mode-in`}
                >
                  Equals
                </button>
                <button
                  onClick={() => onModeChange('not_in')}
                  className={`flex-1 px-2 py-1 transition-colors border-l border-slate-200 ${mode === 'not_in' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`${testId}-mode-not-in`}
                >
                  Not equals
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1 px-0.5">
                {mode === 'not_in' ? 'Show leads NOT matching the selected status.' : 'Show leads matching the selected status.'}
              </p>
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {options.map(opt => {
              const checked = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 flex items-center gap-2"
                  data-testid={`${testId}-opt-${opt.value}`}
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300'}`}>
                    {checked && <Check className="w-2.5 h-2.5" />}
                  </span>
                  <span className="text-slate-700">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ViewLeadsModal = ({ title, filters, onClose, currentUser }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLead, setSelectedLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [metricType, setMetricType] = useState(null);
  // V36 — Backend-side column filters (spans all pages)
  const [trialStatusFilter, setTrialStatusFilter] = useState([]);
  const [trialStatusMode, setTrialStatusMode] = useState('in'); // 'in' | 'not_in'
  const [paymentFilter, setPaymentFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);

  const fetchLeads = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      // If a metric is specified, use the dedicated dashboard/leads endpoint
      // This guarantees the data matches the dashboard KPI numbers exactly
      if (filters.metric) {
        const params = new URLSearchParams();
        params.set('metric', filters.metric);
        if (filters.start_date) params.set('start_date', filters.start_date);
        if (filters.end_date) params.set('end_date', filters.end_date);
        if (filters.user_id) params.set('user_id', filters.user_id);
        if (filters.currency) params.set('currency', filters.currency);  // V42.9: per-currency card drilldown
        if (filters.product) params.set('product', filters.product);    // V17: SoulAI / Baby Tracker purchases
        if (filters.programme) params.set('programme', filters.programme);  // Jun 2026: prenatal / postnatal split
        if (filters.section) params.set('section', filters.section);  // V35.2: section-scope
        if (filters.campaign_id) params.set('campaign_id', filters.campaign_id);  // V36: campaign drilldown
        // V79 — per-section Campaign + Website selectors from the Marketing Dashboard
        if (filters.campaign_keys) params.set('campaign_keys', filters.campaign_keys);
        if (filters.website) params.set('website', filters.website);
        // V36 — column filters (backend-side, spans all pages)
        if (trialStatusFilter.length > 0) {
          params.set('trial_status', trialStatusFilter.join(','));
          params.set('trial_status_mode', trialStatusMode);
        }
        if (paymentFilter.length > 0) params.set('payment', paymentFilter.join(','));
        if (sourceFilter.length > 0) params.set('source', sourceFilter.join(','));
        params.set('page', p.toString());
        params.set('limit', ps.toString());

        // Use custom API base if provided (e.g., sales dashboard), else marketing dashboard
        const apiBase = filters.apiBase || '/api/marketing/dashboard/leads';
        const res = await axios.get(`${API_URL}${apiBase}?${params.toString()}`, { withCredentials: true });
        setLeads(res.data.leads || []);
        setTotal(res.data.total || 0);
        if (res.data.metric_type) setMetricType(res.data.metric_type);
      } else {
        // Fallback to generic leads endpoint for non-metric modals
        const params = new URLSearchParams();
        params.set('limit', ps.toString());
        params.set('page', p.toString());
        if (filters.trial_payment) params.set('trial_payment', filters.trial_payment);
        if (filters.funnel_type) params.set('funnel_type', filters.funnel_type);
        if (filters.enrollment_status) params.set('enrollment_status', filters.enrollment_status);
        if (filters.start_date && filters.end_date) {
          params.set('create_date_filter', 'custom');
          params.set('create_date_from', filters.start_date);
          params.set('create_date_to', filters.end_date);
        }
        const res = await axios.get(`${API_URL}/api/leads?${params.toString()}`, { withCredentials: true });
        setLeads(res.data.leads || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, trialStatusFilter, trialStatusMode, paymentFilter, sourceFilter]);

  // V36 — reset to page 1 whenever any column filter changes
  useEffect(() => { setPage(1); }, [trialStatusFilter, trialStatusMode, paymentFilter, sourceFilter]);

  useEffect(() => { fetchLeads(page, pageSize); }, [page, pageSize, fetchLeads]);

  useEffect(() => {
    if (!selectedLead) return;
    const fetchMeta = async () => {
      try {
        const [usersRes, batchesRes] = await Promise.all([
          axios.get(`${API_URL}/api/users`, { withCredentials: true }),
          axios.get(`${API_URL}/api/batches`, { withCredentials: true }),
        ]);
        setUsers(usersRes.data || []);
        setBatches(batchesRes.data || []);
      } catch (err) {
        console.error('Failed to fetch users/batches:', err);
      }
    };
    if (users.length === 0) fetchMeta();
  }, [selectedLead, users.length]);

  const fmtDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const totalPages = Math.ceil(total / pageSize);
  const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(1); };
  const activeFilterCount = trialStatusFilter.length + paymentFilter.length + sourceFilter.length;

  // V36 — show amount column for revenue / enrolment / renewal metrics
  const showAmountColumn = ['enrolled', 'first_enrolments', 'renewals',
                            'sales_revenue_inr', 'sales_revenue_usd'].includes(metricType);
  // V81 — Refunds drilldown: show what came back and when, not the trial status
  const showRefundLayout = metricType === 'refunds';
  // V42.5 — Sales New/Renewals modals: Lead | Contact | Country | Payment Date | Amount | Source
  const showPaymentLayout = ['sales_new', 'sales_renewals', 'sales_products'].includes(metricType);
  // V17 — Product purchases: one row per purchase, + Plan column
  const showProductLayout = metricType === 'sales_products';
  const noun = showProductLayout ? 'purchase' : 'lead';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="view-leads-modal">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-bold text-slate-900" data-testid="view-leads-modal-title">{title}</h2>
              <p className="text-sm text-slate-500">
                {loading ? 'Loading...' : `${total} ${noun}${total !== 1 ? 's' : ''} found`}
                {activeFilterCount > 0 && (
                  <span className="ml-2 text-indigo-600 font-medium">
                    · {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                  </span>
                )}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="view-leads-modal-close">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            {loading && leads.length === 0 ? (
              <div className="p-12 text-center text-slate-400">Loading leads...</div>
            ) : !loading && leads.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                {activeFilterCount > 0
                  ? 'No leads match the active column filters. Try clearing one.'
                  : 'No leads match the current filters.'}
              </div>
            ) : (
              <table className={`w-full text-sm transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Country</th>
                    {showPaymentLayout ? (
                      <>
                        {showProductLayout && (
                          <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Plan</th>
                        )}
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Payment Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                      </>
                    ) : showRefundLayout ? (
                      <>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Refunded</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Refunded On</th>
                      </>
                    ) : showAmountColumn ? (
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                    ) : (
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                    )}
                    {!showAmountColumn && !showPaymentLayout && !showRefundLayout && (
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                        <span className="inline-flex items-center gap-1">
                          Trial Status
                          <ColumnFilter
                            label="Trial Status"
                            options={TRIAL_STATUS_OPTIONS}
                            selected={trialStatusFilter}
                            onChange={setTrialStatusFilter}
                            mode={trialStatusMode}
                            onModeChange={setTrialStatusMode}
                            testId="col-filter-trial-status"
                          />
                        </span>
                      </th>
                    )}
                    {!showPaymentLayout && (
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                        <span className="inline-flex items-center gap-1">
                          Payment
                          <ColumnFilter
                            label="Payment"
                            options={PAYMENT_OPTIONS}
                            selected={paymentFilter}
                            onChange={setPaymentFilter}
                            testId="col-filter-payment"
                          />
                        </span>
                      </th>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                      <span className="inline-flex items-center gap-1">
                        Source
                        <ColumnFilter
                          label="Source"
                          options={SOURCE_OPTIONS}
                          selected={sourceFilter}
                          onChange={setSourceFilter}
                          testId="col-filter-source"
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <tr key={lead.payment_id || lead.lead_id} className="hover:bg-slate-50 transition-colors" data-testid={`modal-lead-${lead.lead_id}`}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left transition-colors"
                          data-testid={`modal-lead-name-${lead.lead_id}`}
                        >
                          {lead.full_name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-600 text-xs">{lead.email}</div>
                        <div className="text-slate-500 text-xs">{lead.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-600">{lead.country || '-'}</span>
                      </td>
                      {showPaymentLayout ? (
                        <>
                          {showProductLayout && (
                            <td className="px-4 py-3" data-testid={`modal-plan-${lead.payment_id || lead.lead_id}`}>
                              <div className="text-xs font-medium text-slate-800">{lead.product_label || lead.product || '-'}</div>
                              <div className="text-[11px] text-slate-500">{lead.plan_label || '-'}</div>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <span className="text-xs text-slate-600">{fmtDate(lead.payment_date)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-900">
                              {lead.enrolled_currency === 'USD' ? '$'
                                : lead.enrolled_currency === 'CAD' ? 'C$'
                                : lead.enrolled_currency === 'AUD' ? 'A$'
                                : lead.enrolled_currency === 'EUR' ? '€'
                                : lead.enrolled_currency === 'GBP' ? '£'
                                : lead.enrolled_currency && lead.enrolled_currency !== 'INR' ? lead.enrolled_currency + ' '
                                : '₹'}
                              {(lead.enrolled_amount || 0).toLocaleString()}
                            </span>
                          </td>
                        </>
                      ) : showRefundLayout ? (
                        <>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-orange-700" data-testid="refund-amount-cell">
                              −{lead.refunded_currency === 'USD' ? '$' : lead.refunded_currency && lead.refunded_currency !== 'INR' ? lead.refunded_currency + ' ' : '₹'}
                              {(lead.refunded_amount || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-slate-600">{fmtDate(lead.refunded_at)}</span>
                          </td>
                        </>
                      ) : showAmountColumn ? (
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-slate-900">
                            {lead.enrolled_currency === 'USD' ? '$'
                              : lead.enrolled_currency === 'CAD' ? 'C$'
                              : lead.enrolled_currency === 'AUD' ? 'A$'
                              : lead.enrolled_currency === 'EUR' ? '€'
                              : lead.enrolled_currency === 'GBP' ? '£'
                              : lead.enrolled_currency && lead.enrolled_currency !== 'INR' ? lead.enrolled_currency + ' '
                              : '₹'}
                            {(lead.enrolled_amount || 0).toLocaleString()}
                          </span>
                        </td>
                      ) : (
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-600">{fmtDate(lead.created_at)}</span>
                        </td>
                      )}
                      {!showAmountColumn && !showPaymentLayout && !showRefundLayout && (
                        <td className="px-4 py-3">
                          <StatusBadge status={lead.trial_status || 'pending'} size="xs" />
                        </td>
                      )}
                      {!showPaymentLayout && (
                        <td className="px-4 py-3">
                          <StatusBadge
                            status={lead.trial_payment_status === 'paid' ? 'paid' : 'pending'}
                            size="xs"
                          >
                            {lead.trial_payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                          </StatusBadge>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-500">{lead.funnel_type || lead.source || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer with Pagination */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">
                Showing {Math.min((page - 1) * pageSize + 1, total)}-{Math.min(page * pageSize, total)} of {total}
              </span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600"
                data-testid="view-leads-page-size"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="view-leads-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-600 px-2">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="view-leads-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              <a
                href={`/leads`}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium ml-2"
                data-testid="view-leads-modal-open-full"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in Leads Page
              </a>
              <button onClick={onClose} className="btn-secondary text-xs px-3 py-1.5">Close</button>
            </div>
          </div>
        </div>
      </div>

      {selectedLead && (
        <LeadModal
          lead={selectedLead}
          users={users}
          batches={batches}
          currentUser={currentUser}
          onClose={() => setSelectedLead(null)}
          onSave={async () => { setSelectedLead(null); await fetchLeads(page, pageSize); }}
        />
      )}
    </>
  );
};

export default ViewLeadsModal;
